import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import useScreenType from '../../hooks/useScreenType';

// Sub-components
import DesktopAiWatchlist from './DesktopAiWatchlist';
import MobileAiWatchlist from './MobileAiWatchlist';

const AiWatchlistScreen: React.FC = () => {
    const { token, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { isMobile, isTablet } = useScreenType();
    const useMobileLayout = isMobile || isTablet;
    
    const [strategies, setStrategies] = useState<any[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [details, setDetails] = useState<any>(null);
    const [history, setHistory] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [detailsLoading, setDetailsLoading] = useState<boolean>(false);

    const fetchStrategies = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/v1/ai-watchlist/strategies`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setStrategies(data);
                if (data.length > 0) {
                    const defaultId = data[0].id;
                    setSelectedId(defaultId);
                }
            } else {
                console.error("Failed to fetch strategies:", res.status);
            }
        } catch (e) {
            console.error("Error fetching watchlists strategies:", e);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchDetailsAndHistory = useCallback(async (watchlistId: number) => {
        if (!token) return;
        setDetails(null);
        setHistory([]);
        setDetailsLoading(true);
        try {
            const [detailsRes, historyRes] = await Promise.all([
                fetch(`${API_URL}/api/v1/ai-watchlist/${watchlistId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_URL}/api/v1/ai-watchlist/${watchlistId}/history`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);
            
            if (detailsRes.ok) {
                const data = await detailsRes.json();
                setDetails(data);
            } else {
                console.error(`Failed to fetch details for strategy ${watchlistId}:`, detailsRes.status);
            }
            if (historyRes.ok) {
                const data = await historyRes.json();
                setHistory(data);
            } else {
                console.error(`Failed to fetch history for strategy ${watchlistId}:`, historyRes.status);
            }
        } catch (e) {
            console.error("Error fetching strategy details:", e);
        } finally {
            setDetailsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchStrategies();
        } else if (!authLoading) {
            setLoading(false);
        }
    }, [token, authLoading, fetchStrategies]);

    useEffect(() => {
        if (selectedId) {
            fetchDetailsAndHistory(selectedId);
        }
    }, [selectedId, fetchDetailsAndHistory]);

    const handleUpgrade = () => {
        navigate('/upgrade');
    };

    const getRiskLabelStyle = (profile: string) => {
        const baseStyle = {
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase' as const,
            display: 'inline-block'
        };
        switch (profile.toUpperCase()) {
            case 'HIGH':
                return { ...baseStyle, backgroundColor: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' };
            case 'MEDIUM':
                return { ...baseStyle, backgroundColor: 'rgba(16, 185, 129, 0.12)', color: '#10b981' };
            default:
                return { ...baseStyle, backgroundColor: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' };
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '—';
        const d = new Date(dateString);
        return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const isPro = details?.is_pro === true;

    const childProps = {
        strategies,
        selectedId,
        setSelectedId,
        details,
        history,
        loading,
        detailsLoading,
        isPro,
        handleUpgrade,
        formatDate,
        getRiskLabelStyle
    };

    return (
        <SiteLayout>
            <Header title="Watchlist IA ✨" />
            {useMobileLayout ? (
                <MobileAiWatchlist {...childProps} />
            ) : (
                <DesktopAiWatchlist {...childProps} />
            )}
        </SiteLayout>
    );
};

export default AiWatchlistScreen;
